import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export function Card({
  title,
  description,
  children,
  className = "",
  reveal = false,
  revealDelay = 0,
}: {
  title?: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  reveal?: boolean;
  revealDelay?: number;
}) {
  return (
    <section
      className={`ui-card ${className}`}
      {...(reveal
        ? {
            "data-reveal": "",
            style: { "--reveal-delay": `${revealDelay}ms` } as CSSProperties,
          }
        : {})}
    >
      {title ? (
        <header className="ui-card__header">
          <h2>{title}</h2>
          {description ? (
            <p>{description}</p>
          ) : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function Alert({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "warn" | "danger" | "ok";
  title?: string;
  children: ReactNode;
}) {
  const critical = tone === "danger";

  return (
    <div
      className={`ui-alert ui-alert--${tone}`}
      role={critical ? "alert" : "status"}
      aria-live={critical ? "assertive" : "polite"}
    >
      <span className="ui-alert__mark" aria-hidden />
      <div className="ui-alert__content">
        {title ? <p className="ui-alert__title">{title}</p> : null}
        <div className="wrap-anywhere whitespace-pre-line">{children}</div>
      </div>
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="ui-stat">
      <p className="ui-stat__label">{label}</p>
      <p className="ui-stat__value wrap-anywhere tabular-nums">{value}</p>
      {hint ? <p className="ui-stat__hint">{hint}</p> : null}
    </div>
  );
}

export function AddressLine({ label, address, href }: { label: string; address: string; href: string }) {
  return (
    <div className="address-row">
      <span className="address-row__label">{label}</span>
      <code className="address-row__value wrap-anywhere">{address}</code>
      <div className="address-row__actions">
        <CopyAction value={address} label="Salin alamat" />
        <a href={href} target="_blank" rel="noopener noreferrer">
          Lihat kontrak <span aria-hidden>↗</span>
        </a>
      </div>
    </div>
  );
}

export function CopyAction({
  value,
  label = "Salin",
  className = "",
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      className={`copy-action ${copied ? "copy-action--copied" : ""} ${className}`}
      onClick={() => void copy()}
      aria-label={copied ? `${label} berhasil` : label}
    >
      <span className="copy-action__icon" aria-hidden>{copied ? "✓" : null}</span>
      {copied ? "Tersalin" : label}
    </button>
  );
}

export function ReviewRow({
  label,
  children,
  hint,
  emphasis = false,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div className={`review-row ${emphasis ? "review-row--emphasis" : ""}`}>
      <dt>{label}</dt>
      <dd>
        <div>{children}</div>
        {hint ? <small>{hint}</small> : null}
      </dd>
    </div>
  );
}

export function ContractIdentity({
  label,
  address,
  href,
}: {
  label: string;
  address: string;
  href: string;
}) {
  return (
    <div className="contract-identity">
      <p>{label}</p>
      <code>{address}</code>
      <div>
        <CopyAction value={address} label="Salin alamat" />
        <a href={href} target="_blank" rel="noopener noreferrer">
          Lihat di explorer <span aria-hidden>↗</span>
        </a>
      </div>
    </div>
  );
}

const MIGRATION_STEPS = [
  "Wallet",
  "Kelayakan",
  "Izin token",
  "Migrasi",
  "Bukti",
] as const;

export function MigrationRail({
  currentStep,
  completeThrough = Math.max(0, currentStep - 1),
}: {
  currentStep: number;
  completeThrough?: number;
}) {
  return (
    <nav className="migration-rail" aria-label="Tahapan migrasi">
      <p className="migration-rail__title">Alur migrasi</p>
      <ol>
        {MIGRATION_STEPS.map((label, index) => {
          const step = index + 1;
          const state =
            step <= completeThrough ? "complete" : step === currentStep ? "current" : "pending";

          return (
            <li key={label} className={`migration-rail__step migration-rail__step--${state}`}>
              <span className="migration-rail__number">
                {state === "complete" ? "✓" : String(step).padStart(2, "0")}
              </span>
              <span>
                <strong>{label}</strong>
                <small>
                  {state === "complete" ? "Selesai" : state === "current" ? "Tahap aktif" : "Menunggu"}
                </small>
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function TransactionPlate({
  step,
  title,
  description,
  children,
  tone = "default",
}: {
  step: string;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  tone?: "default" | "warning" | "danger" | "success";
}) {
  return (
    <section className={`transaction-plate transaction-plate--${tone}`}>
      <header className="transaction-plate__header">
        <p>{step}</p>
        <h2>{title}</h2>
        {description ? <div className="transaction-plate__description">{description}</div> : null}
      </header>
      <div className="transaction-plate__body">{children}</div>
    </section>
  );
}

export function ProofStamp({
  title,
  record,
  children,
  actions,
}: {
  title: string;
  record: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="proof-stamp" aria-label={title}>
      <header>
        <span aria-hidden>✓</span>
        <div>
          <p>ASETQU / ON-CHAIN RECORD</p>
          <h2>{title}</h2>
        </div>
      </header>
      <strong className="proof-stamp__record">{record}</strong>
      <dl>{children}</dl>
      {actions ? <div className="proof-stamp__actions">{actions}</div> : null}
    </section>
  );
}

export function PageHeader({
  context,
  title,
  description,
  meta,
  tone = "default",
}: {
  context: string;
  title: string;
  description: ReactNode;
  meta?: ReactNode;
  tone?: "default" | "danger";
}) {
  return (
    <header className={`page-intro page-intro--${tone}`}>
      <div className="page-intro__copy">
        <p className="page-intro__context">
          <span aria-hidden />
          {context}
        </p>
        <h1>{title}</h1>
        <p className="page-intro__description">{description}</p>
      </div>
      {meta ? <div className="page-intro__meta">{meta}</div> : null}
    </header>
  );
}

export function Button({
  children,
  onClick,
  disabled,
  type = "button",
  variant = "primary",
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "danger" | "gold";
}) {
  const styles = {
    primary: "ui-button--primary",
    secondary: "ui-button--secondary",
    danger: "ui-button--danger",
    gold: "ui-button--gold",
  }[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`ui-button ${styles}`}
    >
      {children}
    </button>
  );
}
